import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Stack,
    Button,
    TablePagination,
    Chip,
    IconButton,
    Tooltip,
    Checkbox,
    CircularProgress,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Menu,
    MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { memo, useCallback, useMemo, useState } from 'react';
import { deleteRuleById, deleteRules, getGitDiff, masterDisable, UpdateRules, updateRuleState } from '../../../controllers/RuleController';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import MenuIcon from "@mui/icons-material/Menu";
import { RiDiscountPercentFill } from "react-icons/ri";
import { SiLightning } from "react-icons/si";
import { MdCleaningServices } from "react-icons/md";
import { useSelector } from 'react-redux';
import { formatUtcDate, formatUtcDateTime } from '../../../utils/dateTime';
import { ROLES, hasAnyRole } from '../../../utils/roles';
import { getRuleStatus } from './RuleUtils';

const columns = [
    { label: "Rule Id", id: "id", numeric: false, disablePadding: true, },
    { label: "Rule Title", id: "description", numeric: false, disablePadding: true, },
    { label: "Rule Type", id: "ruleType", numeric: false, disablePadding: true, },
    { label: "Priority", id: "priority", numeric: true, disablePadding: true, },
    { label: "State", id: "ruleState", numeric: false, disablePadding: true, },
    { label: "Status", id: "status", numeric: false, disablePadding: true, },
    { label: "Start Date", id: "startDate", numeric: false, disablePadding: true, },
    { label: "End Date", id: "endDate", numeric: false, disablePadding: true, },
    { label: "Last Modified", id: "timeUpdated", numeric: false, disablePadding: true, },
    { label: "Last Published", id: "lastPublished", numeric: false, disablePadding: true, },
    { label: "Created By", id: "createdBy", numeric: false, disablePadding: true, },
    { label: "Approved By", id: "approvedBy", numeric: false, disablePadding: true, },
    { label: "Staging Version", id: "ruleJson.stagingCommitId", numeric: false, disablePadding: true, },
    { label: "Published Version", id: "ruleJson.publishedCommitId", numeric: false, disablePadding: true, },
    { label: "Action", id: "action", numeric: false, disablePadding: true, }
];

// Pure helpers - hoisted out of the component so they aren't recreated on
// every render, and so the memoized row below can call them directly.
const getNested = (obj, path) =>
    String(path || "").split(".").reduce((o, p) => (o && p in o ? o[p] : undefined), obj);

const getLabel = (action) => {
    let text = '';
    switch (action) {
        case "PUBLISHED":
            text = "publish";
            break;
        case 'EDITING':
            text = "cancel request to publish";
            break;
        case 'PUBLISH_REQUESTED':
            text = "request to publish";
            break;
        case 'PUBLISH_APPROVED':
            text = "publish";
            break;
        case 'DELETED':
            text = "delete";
            break;
        case 'MASTER_DISABLED':
            text = "master disable";
            break;
        case 'MASTER_ENABLED':
            text = "enable";
            break;

    }
    return text;
};

const getRuleTypeMeta = (ruleType) => {
    switch (ruleType) {
        case "FLASH_SALE":
            return { label: "Flash Sale", bg: "#1565c0", color: "#fff" };
        case "LIQUIDATION_SALE":
            return { label: "Liquidation Sale", bg: "#c66b6b", color: "#fff" };

        case "DISCOUNTED_PRICING":
            return { label: "Discounted Pricing", bg: "#7b1fa2", color: "#fff" };
        default:
            return {
                label: ruleType || "-",
                bg: "#546e7a",
                color: "#fff",
            };
    }
};

const getRuleTypeIcons = (ruleType) => {
    switch (ruleType) {
        case "FLASH_SALE":
            return <SiLightning style={{ color: "#1565c0" }} size={"2em"} />;
        case "LIQUIDATION_SALE":
            return <MdCleaningServices style={{ color: "#c66b6b" }} size={"2em"} />;
        case "DISCOUNTED_PRICING":
            return <RiDiscountPercentFill style={{ color: "#7b1fa2" }} size={"2em"} />;
        default:
            return null;
    }
};

const ActionIconButton = ({ title, ariaLabel, icon, onClick, color = "primary", size = "small" }) => (
    <Tooltip title={title}>
        <IconButton
            size={size}
            onClick={onClick}
            aria-label={ariaLabel}
            color={color}
        >
            {icon}
        </IconButton>
    </Tooltip>
);

const getActionButtons = (rule, { canApproveRules, navigate, onRequestAction, status }) => {
    const navigateToRule = () => navigate(`/pricing-rules/${rule.id}`, {
        state: { selectedCatalogId: rule.catalogId }
    });
    const publishedCommitId = rule.ruleJson?.publishedCommitId;

    const historyButton = (
        <ActionIconButton
            key="history"
            title="View History"
            ariaLabel="history"
            icon={<HistoryIcon fontSize="small" />}
            onClick={() => navigate(`/audit?entityId=${rule.id}&entityType=RULE`)}
        />
    );
    if (rule.ruleType === "LIQUIDATION_SALE" || status === "COMPLETED") {
        return [
            <ActionIconButton
                key="view"
                title="View Rule"
                ariaLabel="view"
                icon={<VisibilityIcon fontSize="small" />}
                onClick={navigateToRule}
            />,
            historyButton
        ];
    }

    switch (rule.ruleState) {
        case "PUBLISHED":
            return [
                <ActionIconButton
                    key="edit"
                    title="Edit"
                    ariaLabel="edit"
                    icon={<EditIcon fontSize="small" />}
                    onClick={navigateToRule}
                />,
                historyButton
            ];
        case "PUBLISH_REQUESTED":
            return [
                <ActionIconButton
                    key="view"
                    title="View Rule"
                    ariaLabel="view"
                    icon={<VisibilityIcon fontSize="small" />}
                    onClick={navigateToRule}
                />,
                ...(canApproveRules ? [
                    <ActionIconButton
                        key="approve"
                        title="Approve Publish Request"
                        ariaLabel="approve"
                        color="success"
                        icon={<CheckIcon fontSize="small" />}
                        onClick={() => onRequestAction(rule, "PUBLISH_APPROVED")}
                    />
                ] : []),
                <ActionIconButton
                    key="cancel"
                    title="Cancel Publish Request"
                    ariaLabel="cancel"
                    color="warning"
                    icon={<CancelIcon fontSize="small" />}
                    onClick={() => onRequestAction(rule, "EDITING")}
                />,
                historyButton
            ];
        case "EDITING": {
            const buttons = [
                <ActionIconButton
                    key="request-publish"
                    title="Request Publish"
                    ariaLabel="request publish"
                    icon={<PublishIcon />}
                    onClick={() => onRequestAction(rule, "PUBLISH_REQUESTED")}
                />,
                <ActionIconButton
                    key="edit"
                    title="Edit"
                    ariaLabel="edit"
                    icon={<EditIcon fontSize="small" />}
                    onClick={navigateToRule}
                />
            ];

            if (!publishedCommitId) {
                buttons.push(
                    <ActionIconButton
                        key="delete"
                        title="Delete Rule"
                        ariaLabel="delete"
                        color="error"
                        icon={<DeleteIcon fontSize="small" />}
                        onClick={() => onRequestAction(rule, "DELETED")}
                    />
                );
            }

            buttons.push(historyButton);
            return buttons;
        }
        case "PUBLISH_APPROVED":
            return [
                <ActionIconButton
                    key="view"
                    title="View Rule"
                    ariaLabel="view"
                    icon={<VisibilityIcon fontSize="small" />}
                    onClick={navigateToRule}
                />,
                historyButton
            ];
        default:
            return [historyButton];
    }
};

// One table row, memoized so a re-render triggered elsewhere in the page
// (opening the bulk-action menu, the diff dialog, the confirm dialog, a
// pagination fetch) doesn't force every row to recompute its status chip
// and action buttons - only rows whose own props actually changed re-render.
const RuleTableRow = memo(function RuleTableRow({
    row,
    isRowSelected,
    onRowClick,
    canApproveRules,
    navigate,
    onRequestAction,
    onViewDiff,
    timezone,
}) {
    const status = getRuleStatus(row);
    const actionButtons = getActionButtons(row, { canApproveRules, navigate, onRequestAction, status });
    const isCompleted = status === "COMPLETED";
    const isLiquidationSale = row.ruleType === "LIQUIDATION_SALE";
    const isUnselectable = isCompleted || isLiquidationSale;
    const unselectableReason = isLiquidationSale
        ? "Liquidation Sale rules cannot be selected"
        : isCompleted
            ? "Completed rules cannot be selected"
            : "";

    return (
        <TableRow
            hover
            sx={{
                "&:nth-of-type(even)": {
                    backgroundColor: "action.hover",
                },
                transition: "all 0.2s ease",
            }}
        >
            <TableCell padding="checkbox">
                <Tooltip title={unselectableReason}>
                    <span>
                        <Checkbox
                            color="primary"
                            checked={isRowSelected}
                            disabled={isUnselectable}
                            onChange={() => onRowClick(row)}
                        />
                    </span>
                </Tooltip>
            </TableCell>
            {columns.map((col, idx) => {
                if (col.id === "ruleType") {
                    const typeMeta = getRuleTypeMeta(row.ruleType);
                    return (
                        <TableCell key={idx} align="left">
                            <Tooltip title={typeMeta.label}>
                                {getRuleTypeIcons(row.ruleType) || (
                                    <Chip
                                        label={typeMeta.label}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: typeMeta.color,
                                            backgroundColor: typeMeta.bg
                                        }}
                                    />
                                )}
                            </Tooltip>
                        </TableCell>
                    );
                }
                if (col.id === 'status' && status) {
                    return (<TableCell key={idx} align="left">
                        <Chip
                            label={status}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#fff",
                                px: 0.5,
                                backgroundColor:
                                    status === "ONGOING"
                                        ? "#2e7d32"
                                        : status === "UPCOMING"
                                            ? "#ed6c02"
                                            : "#6c757d"
                            }}
                        />
                    </TableCell>)
                }
                if (col.id === "action") {
                    return (
                        <TableCell key={idx} align="left">
                            <Stack direction="row" spacing={1}>
                                {actionButtons}
                            </Stack>
                        </TableCell>
                    );
                }
                if (col.id === "ruleState") {
                    return (<TableCell key={idx} align={col.numeric ? "right" : "left"}>
                        <Chip label={row.ruleState} />
                    </TableCell>)

                }
                if (col.id === "ruleJson.publishedCommitId") {
                    const publishedCommit = getNested(row, "ruleJson.publishedCommitId");
                    const shortCommit = publishedCommit ? publishedCommit.slice(0, 7) : "-";

                    return (
                        <TableCell key={idx}>
                            {publishedCommit && row.commitsBehind > 0 ? (
                                <Button
                                    size="small"
                                    onClick={() => onViewDiff(row)}
                                    sx={{ textTransform: "none", p: 0 }}
                                >
                                    {shortCommit} ({row.commitsBehind} commit(s) behind)
                                </Button>
                            ) : (
                                shortCommit
                            )}
                        </TableCell>
                    );
                }
                if (col.id === "timeUpdated") {
                    return (
                        <TableCell key={idx} align={col.numeric ? "right" : "left"}>
                            {formatUtcDateTime(row.timeUpdated, timezone)}
                        </TableCell>
                    );
                }
                if (col.id === "lastPublished") {
                    return (
                        <TableCell key={idx} align={col.numeric ? "right" : "left"}>
                            {row.ruleState === "PUBLISHED"
                                ? formatUtcDateTime(row.timeUpdated, timezone)
                                : "-"}
                        </TableCell>
                    );
                }
                const value = col.id ? getNested(row, col.id) : undefined;
                const display = (col.id === "startDate" || col.id === "endDate")
                    ? formatUtcDate(value, timezone)
                    : (value == null || value === "" ? "-" : value);

                return (
                    <TableCell key={idx} align={col.numeric ? "right" : "left"}>
                        {display}
                    </TableCell>
                );
            })}
        </TableRow>
    );
});

export default function PricingRuleList(props) {

    const { rules, listRules, loading, page, totalElements, size, setSize, setPage, setLoading, setApiMessage } = props;
    const [selectedRules, setSelectedRules] = useState([]);
    const timezone = useSelector((state) => state.globalData.timezone);
    const userRoles = useSelector((state) => state.globalData.userRoles);
    const canApproveRules = hasAnyRole(userRoles, [ROLES.RULE_APPROVER]);

    const [confirmState, setConfirmState] = useState({
        open: false,
        action: null,
        rules: [],
        label: null
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const [diffDialog, setDiffDialog] = useState({
        open: false,
        loading: false,
        content: ""
    });
    const openMenu = Boolean(anchorEl);

    const selectedIds = useMemo(() => new Set(selectedRules.map((r) => r.id)), [selectedRules]);

    // Liquidation Sale rules can't be selected at all - individually
    // (checkbox is disabled per-row, see RuleTableRow) or via "select all" -
    // bulk actions (publish, master disable, delete) aren't meant to apply
    // to them.
    const selectableRules = useMemo(
        () => rules.filter((r) => getRuleStatus(r) !== "COMPLETED" && r.ruleType !== "LIQUIDATION_SALE"),
        [rules]
    );

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleViewDiff = useCallback(async (rule) => {
        try {
            setDiffDialog({ open: true, loading: true, content: "" });

            const diff = await getGitDiff(rule.id);

            setDiffDialog({
                open: true,
                loading: false,
                content: diff,
                ruleId: rule.id,
                ruleTitle: rule.description
            });
        } catch {
            setDiffDialog({
                open: true,
                loading: false,
                content: "Failed to load diff",
                ruleId: null,
                ruleTitle: ""
            });
        }
    }, []);

    const getBulkActions = () => {
        if (selectedRules.length === 0) return [];

        const states = selectedRules.map(r => r.ruleState);

        const allEditing = states.every(s => s === "EDITING");
        const allRequested = states.every(s => s === "PUBLISH_REQUESTED");
        const isMasterDisabled = selectedRules.every(s => s.masterDisable && (s.ruleState === "PUBLISHED" || s.ruleState === "EDITING"));
        const isMasterEnabled = selectedRules.every(s => !s.masterDisable && (s.ruleState === "PUBLISHED" || s.ruleState === "EDITING"));
        const isDeleting = allEditing && selectedRules.every(s => !s.ruleJson.publishedCommitId);

        const actions = [];

        if (allEditing) {
            actions.push({ label: "Request Publish", value: "PUBLISH_REQUESTED" });
        }

        if (allRequested) {
            if (canApproveRules) {
                actions.push({ label: "Approve Publish", value: "PUBLISH_APPROVED" });
            }
            actions.push({ label: "Cancel Publish", value: "EDITING" });
        }
        if (isMasterEnabled) {
            actions.push({ label: "Master Disable", value: "MASTER_DISABLED" });
        }
        if (isMasterDisabled) {
            actions.push({ label: "Enable", value: "MASTER_ENABLED" });
        }
        if (isDeleting) {
            actions.push({ label: "Delete", value: "DELETED" });
        }

        return actions;
    };

    const handleBulkAction = (action) => {
        handleCloseMenu();
        let label = getLabel(action);
        setConfirmState({
            open: true,
            action,
            rules: selectedRules,
            label
        });
    };

    const navigate = useNavigate();

    const handleRequestAction = useCallback((rule, action) => {
        const label = getLabel(action);
        setConfirmState({
            open: true,
            action,
            rules: [rule],
            label
        });
    }, []);

    const handleConfirmAction = async () => {
        const { rules, action } = confirmState;
        if (rules.length === 1) {
            if (action === "DELETED") {
                handleDelete(rules[0].id);
                return;
            }
            if (action === "MASTER_DISABLED" || action === "MASTER_ENABLED") {
                handleMasterDisable(rules.map(r => r.id), action === "MASTER_DISABLED");
                return;
            }
            try {
                setLoading(true);
                await updateRuleState(rules[0].id, action);
                setApiMessage({
                    open: true,
                    type: "success",
                    message: "Successfully updated"
                });
                listRules();
            } catch (err) {
                setApiMessage({
                    open: true,
                    type: "error",
                    message: err?.message || "Something went wrong",
                });
            }
            setLoading(false);
        } else {
            if (action === "DELETED") {
                handleDeleteMultiple(rules.map(r => r.id));
                return;
            }
            if (action === "MASTER_DISABLED" || action === "MASTER_ENABLED") {
                handleMasterDisable(rules.map(r => r.id), action === "MASTER_DISABLED");
                return;
            }
            try {
                setLoading(true);
                await UpdateRules(rules.map(r => r.id), action);
                setApiMessage({
                    open: true,
                    type: "success",
                    message: "Successfully updated"
                });
                listRules();
                setSelectedRules([]);

            } catch (err) {
                setApiMessage({
                    open: true,
                    type: "error",
                    message: err?.message || "Something went wrong",
                });
            }
            setLoading(false);

        }
        handleCloseConfirm();
    }

    const handleCloseConfirm = () => {
        setConfirmState({
            open: false,
            action: null,
            rules: [],
            label: null
        });
    }

    const handleDeleteMultiple = async (ruleIds) => {
        try {
            await deleteRules(ruleIds);
            setApiMessage({
                open: true,
                type: "success",
                message: "Successfully deleted"
            });
            listRules();
            setSelectedRules([]);
        } catch (err) {
            setApiMessage({
                open: true,
                type: "error",
                message: err?.message || "Something went wrong",
            });
        }
        handleCloseConfirm();
    }

    const handleDelete = async (ruleId) => {
        try {
            await deleteRuleById(ruleId);
            setApiMessage({
                open: true,
                type: "success",
                message: "Successfully deleted"
            });
            listRules();
        } catch (err) {
            setApiMessage({
                open: true,
                type: "error",
                message: err?.message || "Something went wrong",
            });
        }
        handleCloseConfirm();
    }

    const handleMasterDisable = async (ruleIds, masterDisableValue) => {
        try {
            await masterDisable(ruleIds, masterDisableValue);
            setApiMessage({
                open: true,
                type: "success",
                message: `Successfully ${masterDisableValue ? "disabled" : "enabled"}`
            });
            listRules();
            setSelectedRules([]);
        } catch (err) {
            setApiMessage({
                open: true,
                type: "error",
                message: err?.message || "Something went wrong",
            });
        }
        handleCloseConfirm();
    }

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            setSelectedRules(selectableRules);
            return;
        }
        setSelectedRules([]);
    };

    const handleRowClick = useCallback((item) => {
        setSelectedRules((prev) => {
            const exists = prev.some((r) => r.id === item.id);
            return exists ? prev.filter((r) => r.id !== item.id) : [...prev, item];
        });
    }, []);

    return (
        <>
            <Paper
                // elevation={2}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    overflow: "hidden",
                }}
            >
                {/* Scrollable Table Area */}
                {selectedRules.length > 0 && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            px: 2,
                            py: 1.5,
                            backgroundColor: "action.selected",
                            borderBottom: "1px solid",
                            borderColor: "divider"
                        }}
                    >
                        <Typography fontWeight={600}>
                            {selectedRules.length} rule(s) selected
                        </Typography>

                        <IconButton onClick={handleOpenMenu}>
                            <MenuIcon />
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={openMenu}
                            onClose={handleCloseMenu}
                        >
                            {getBulkActions().length === 0 && (
                                <MenuItem disabled>No common actions available</MenuItem>
                            )}

                            {getBulkActions().map((action) => (
                                <MenuItem
                                    key={action.value}
                                    onClick={() => handleBulkAction(action.value)}
                                >
                                    {action.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Stack>
                )}
                <TableContainer
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: "auto",
                    }}
                >
                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            minWidth: 1400,
                            "& .MuiTableCell-root": {
                                py: 1.5,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    padding="checkbox"
                                    sx={{
                                        color: "primary.main",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <Checkbox
                                        indeterminate={
                                            selectedRules.length > 0 &&
                                            selectedRules.length < selectableRules.length
                                        }
                                        checked={
                                            selectableRules.length > 0 &&
                                            selectedRules.length === selectableRules.length
                                        }
                                        disabled={selectableRules.length === 0}
                                        onChange={handleSelectAllClick}
                                    />
                                </TableCell>

                                {columns.map((col, ind) => (
                                    <TableCell
                                        key={ind}
                                        align="left"
                                        sx={{
                                            color: "primary.main",
                                            fontWeight: 600,
                                            // whiteSpace: "nowrap",
                                            // position: "sticky",
                                            // top: 0,
                                            // zIndex: 2,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rules.map((row) => (
                                <RuleTableRow
                                    key={row.id}
                                    row={row}
                                    isRowSelected={selectedIds.has(row.id)}
                                    onRowClick={handleRowClick}
                                    canApproveRules={canApproveRules}
                                    navigate={navigate}
                                    onRequestAction={handleRequestAction}
                                    onViewDiff={handleViewDiff}
                                    timezone={timezone}
                                />
                            ))}
                        </TableBody>
                    </Table>


                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalElements}
                    sx={{
                        borderTop: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "#fff",
                    }}
                    page={page}
                    onPageChange={(event, newPage) => { setPage(newPage); setSelectedRules([]) }}
                    rowsPerPage={size}
                    onRowsPerPageChange={(event) => {
                        setSize(parseInt(event.target.value, 10));
                        setPage(0);
                        setSelectedRules([]);
                    }}
                />
            </Paper >
            <ConfirmDialog
                open={confirmState.open}
                title="Confirm Action"
                message={
                    confirmState.rules.length === 1
                        ? `Are you sure you want to ${confirmState.label} rule "${confirmState.rules[0].id}"?`
                        : `Are you sure you want to ${confirmState.label} ${confirmState.rules.length} selected rules?`
                }
                confirmText="Yes"
                confirmColor={
                    confirmState.action === "DELETED" ? "error" : "primary"
                }
                loading={loading}
                onClose={handleCloseConfirm}
                onConfirm={handleConfirmAction}
            />

            <Dialog
                open={diffDialog.open}
                onClose={() => setDiffDialog({ open: false })}
                maxWidth={false}
                scroll="paper"
                slotProps={{
                    paper: {
                        sx: {
                            width: "90vw",
                            maxWidth: "90vw",
                            maxHeight: "85vh",
                        }
                    }
                }}
            >
                <DialogTitle>
                    Version Difference — Rule ID: {diffDialog.ruleId} · {diffDialog.ruleTitle}
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{ p: 0, overflow: "auto" }}
                >
                    {diffDialog.loading ? (
                        <Stack alignItems="center" justifyContent="center" py={6}>
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <Box
                            sx={{
                                p: 2,
                                fontFamily: "monospace",
                                fontSize: 13,
                                whiteSpace: "pre",
                                minWidth: "max-content",
                            }}
                        >
                            {diffDialog.content?.split("\n").map((line, index) => {

                                let style = {};

                                if (line.startsWith("+") && !line.startsWith("+++")) {
                                    style = { backgroundColor: "#e6ffed", color: "#1b5e20" };
                                } else if (line.startsWith("-") && !line.startsWith("---")) {
                                    style = { backgroundColor: "#ffeef0", color: "#b71c1c" };
                                } else if (line.startsWith("@@")) {
                                    style = { backgroundColor: "#f6f8fa", color: "#6a737d", fontWeight: 600 };
                                }

                                return (
                                    <div key={index} style={{ padding: "2px 6px", ...style }}>
                                        {line}
                                    </div>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setDiffDialog({ open: false })} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );
}
